import {Application} from '@app/views/admin/application-settings/models/application.model';
import {DirectoryOrganization} from '@app/core/model/entity-profile';
import {ProcessHistoryState} from '@app/views/admin/components/sync-history-timeline/models/process-history-state.model';
import {environment} from '@env/environment';

export interface IJob {
    id: string;
    processId: number;
    processName: string;
    nodeId: string;
    eventBody: string;
    eventClass: string;
    weight: number;
    creationTs: Date;
    startExecution: Date;
    endExecution: Date;
    averageDurationExecutionInMinutes: number;
    applicationGuid: string;
    integrationId: string;
    defaultApplicationGuid: string;
    districtGuid: string;
    priority: number;
    jobType: JOB_TYPE;
    state: ProcessHistoryState;
    sessionId: number;
    statId: number;
    details: string;

    progress?: number;
    duration?: number;
    expectedRemainingTime?: number;
    application?: Application;
    organization?: DirectoryOrganization;
}

export enum JOB_TYPE {
    RosterDataSyncJob = 'RosterDataSyncJob',
    EventJob = 'EventJob',
    RosterDataAccessJob = 'RosterDataAccessJob',
    SystemJob = 'SystemJob',
}

export const JOB_PRIORITY = {
    LOW: 1,
    MEDIUM: 2,
    HIGH: 3,
    HIGHEST: 4,
};

export const JOB_PRIORITY_FRIENDLY_NAMES = {
    1: 'Low',
    2: 'Medium',
    3: 'High',
    4: 'Highest'
};

export enum PROCESS_NAME {
    EXPIRE_TRANSACTIONS = 'EXPIRE_TRANSACTIONS',
    DataSourceCheckConfigurationProcess = 'DataSourceCheckConfigurationProcess',
    'Expired integrations process' = 'Expired integrations process',
    'Send notifications process' = 'Send notifications process',
    SessionBuilderProcess = 'SessionBuilderProcess',
    ABOLISH_DEAD_PROCES = 'ABOLISH_DEAD_PROCES',
    ARC_PROCESS_STAT = 'ARC_PROCESS_STAT',
    HOUSE_KEEPING = 'HOUSE_KEEPING',
    SES_ANALYZER = 'SES_ANALYZER',
    'Check data access consistency' = 'Check data access consistency',
    SchoolYearRollOverProcess = 'SchoolYearRollOverProcess',
    'GG4L Catalyst Catalog Sync' = 'GG4L Catalyst Catalog Sync',
    'Notification expiration process' = 'Notification expiration process',
    IntegrationProcessConfig = 'IntegrationProcessConfig',
    'IntegrationProcessConfig Periodical' = 'IntegrationProcessConfig Periodical',
}

export const JobTypesFriendlyNamesMap = new Map<JOB_TYPE, string>([
    [JOB_TYPE.RosterDataAccessJob, 'Data Access'],
    [JOB_TYPE.RosterDataSyncJob, 'Data Sync'],
    [JOB_TYPE.EventJob, 'Delete Organization'],
    [JOB_TYPE.SystemJob, 'System'],
]);

export const ProcessFriendlyNamesMap = new Map<PROCESS_NAME, {friendlyName: string; tooltip: string}>([
    [PROCESS_NAME.EXPIRE_TRANSACTIONS, {friendlyName: 'Expire transactions', tooltip: `Validate active transactions and rollback those that didn't meet deadline`}],
    [PROCESS_NAME.DataSourceCheckConfigurationProcess, {friendlyName: 'Check data sources configuration', tooltip: 'Check data sources configuration'}],
    [PROCESS_NAME['Expired integrations process'], {friendlyName: 'Expired integrations', tooltip: 'Suspend access to data for expired integrations'}],
    [PROCESS_NAME['Send notifications process'], {friendlyName: 'Send notifications', tooltip: 'Send daily notification'}],
    [PROCESS_NAME.SessionBuilderProcess, {friendlyName: 'Session builder', tooltip: 'Create processes based on collected snapshots'}],
    [PROCESS_NAME.ABOLISH_DEAD_PROCES, {friendlyName: 'Abolish dead process', tooltip: 'Kill processes which passed their deadline'}],
    [PROCESS_NAME.ARC_PROCESS_STAT, {friendlyName: 'Archive process stat', tooltip: 'Move process statistic and messages into archive table'}],
    [PROCESS_NAME.HOUSE_KEEPING, {friendlyName: 'House keeping', tooltip: 'Clean some tables'}],
    [PROCESS_NAME.SES_ANALYZER, {friendlyName: 'SES analyzer', tooltip: 'Analyze SES rating and normalize it if needed'}],
    [PROCESS_NAME['Check data access consistency'], {friendlyName: 'Check data access consistency', tooltip: 'Check data access consistency between district and vendor'}],
    [PROCESS_NAME.SchoolYearRollOverProcess, {friendlyName: 'School Year Rollover', tooltip: 'Process that disable data deletion or suspend data import for organization when school year period began'}],
    [PROCESS_NAME['GG4L Catalyst Catalog Sync'], {friendlyName: `${environment.customer.customerShortName} Catalyst Catalog Sync`, tooltip: `${environment.customer.customerShortName} Catalyst Catalog Sync`}],
    [PROCESS_NAME['Notification expiration process'], {friendlyName: 'Notification expiration', tooltip: 'Notify district if district-vendor integration expired soon'}],
    [PROCESS_NAME.IntegrationProcessConfig, {friendlyName: 'Manual', tooltip: 'Manual Data Sync'}],
    [PROCESS_NAME['IntegrationProcessConfig Periodical'], {friendlyName: 'Periodical', tooltip: 'Periodical Data Sync'}],
]);

export const MAX_DATA_ACCESS_RUNNING_JOBS = 5;
export const MAX_DATA_ACCESS_RUNNING_JOBS_EXTENDED = 10;
